import { reduce } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { catchError, switchMap, share, first, map, finalize, take, tap } from 'rxjs/operators';
import { StsSelectors } from 'src/app/@store/selectors/sts.selectors';
import { ClaimValue } from 'src/app/plasma-ui-common/enums/claim-value.enum';
import { ApiService } from 'src/app/services/api/api.service';
import { AuthService } from 'src/app/services/auth/auth.service';
import { ChangeEmailService } from 'src/app/services/change-email.service';
import { ConfigService } from 'src/app/services/config/config.service';
import { ErrorHandlerService } from 'src/app/services/error-handler.service';
import { COLUMBIA_TENANT, isColumbiaTenant, readOrgId } from 'src/app/services/config/nested-tenant';
import { WhiteLabelNames } from 'src/app/plasma-ui-common/enums/white-label.enum';

const CONFIRM_EMAIL_API = '/manage/accounts/v3/confirm-email';

/**
 * Query param the activation link carries when it was delivered by SMS rather than e-mail.
 * IT accounts registered against a codice fiscale hold no address, so the back end texts the
 * link instead (SA-20925) and stamps the channel on it - the only thing this page has to go
 * on, since a link opened on the phone that received it shares nothing with the browser the
 * account was registered in. It is passed on to the login page so the banner waiting there
 * can say the account was verified rather than the e-mail address.
 */
const SMS_CHANNEL = 'sms';

/**
 * activation-status for a confirm-email failure this page has no specific message for.
 * The login page renders a banner per status it knows and nothing at all for one it does
 * not, so an unknown code leaves the plain login page - the honest reading of a failure
 * whose cause this page cannot name, and the one thing the 'success' default must not do.
 */
const UNKNOWN_FAILURE_STATUS = 'failed';

@Component({
    selector: 'syn-activate-account',
    templateUrl: './activate-account.component.html',
    styleUrls: ['./activate-account.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class ActivateAccountComponent implements OnInit, OnDestroy {
  loading$: BehaviorSubject<boolean> = new BehaviorSubject(true);

  success$: Observable<boolean> = this.activeRt.queryParamMap.pipe(
    first(),
    share(),
    tap(params => {
      this.isSmsActivation = params.get('channel')?.toLowerCase() === SMS_CHANNEL;
    }),
    switchMap(params =>
      this.putConfirmEmail(
        params.get('sequenceId') || params.get('seqId'),
        params.get('id'),
        params.get('change-email-verify'),
      ),
    ),
    finalize(() =>
      this.redirectsToLogin$
        .pipe(take(1))
        .subscribe(redirects =>
          redirects ? this.shfyActivationRedirect() : this.loading$.next(false),
        ),
    ),
  );
  private hasRedirected: boolean = false;

  /** Whether the link that opened this page was the one sent by SMS - see SMS_CHANNEL */
  private isSmsActivation: boolean = false;

  /**
   * Where to send the user to resume the mobile app's pending login, when the account being
   * activated here signed up from the app (SA-21772). Null for every other activation, which
   * is what leaves their existing redirect to the login page exactly as it was.
   *
   * Held rather than acted on: the activation that produced it may have been opened days later
   * in a different browser on a different device, so the hand-off is offered as something the
   * user taps. A real tap is also the more dependable trigger for a custom URL scheme than a
   * scripted navigation, which some mobile browsers decline outright.
   */
  mobileAppRedirectUrl: string | null = null;

  error$: BehaviorSubject<string | null> = new BehaviorSubject(null);

  loginLink$: Observable<string> = this.config.envConfig$.pipe(map(c => c.BaseAuthUrl));
  isSHFYInstance$ = this.store.select(StsSelectors.isUkShfyInstance);
  isITInstance$ = this.store.select(StsSelectors.isITTenant);
  isGlobal$ = this.store.select(StsSelectors.isGlobalTenant);
  private readonly isColumbia$ = this.config.envConfig$.pipe(map(c => isColumbiaTenant(c?.WhiteLabelName)));

  /** Tenants whose activation ends on the STS login page, which shows the outcome banner */
  private readonly redirectsToLogin$ = combineLatest([
    this.isSHFYInstance$,
    this.isITInstance$,
    this.isGlobal$,
    this.isColumbia$,
  ]).pipe(map(tenants => tenants.some(Boolean)));
  isActiveUser: boolean = false;
  isLinkExpired: boolean = false;
  isDuplicateError: boolean = false;
  isChangeEmailLinkExpired: boolean = false;

  constructor(
    private readonly store: Store,
    private readonly activeRt: ActivatedRoute,
    private readonly api: ApiService,
    private readonly config: ConfigService,
    private readonly changeEmail: ChangeEmailService,
    private readonly auth: AuthService,
    private readonly errorHandlerService: ErrorHandlerService,
  ) {}

  ngOnInit(): void {
    this.errorHandlerService.disableError = true;
  }

  ngOnDestroy(): void {
    this.errorHandlerService.disableError = false;
  }

  // eslint-disable-next-line max-lines-per-function
  private putConfirmEmail(seqId: string, id: string, isChangeEmail?: string): Observable<boolean> {
    isChangeEmail = <any>(isChangeEmail?.toLowerCase() === 'true');
    const endpoint$ = isChangeEmail
      ? this.changeEmail.verifyEmailLink(id, seqId)
      : this.api.put(CONFIRM_EMAIL_API, { id, seqId, sequenceId: seqId });

    return endpoint$.pipe(
      map(res => {
        // SA-21772: only an activation whose sign-up started from the mobile app carries one,
        // and only then is the hand-off offered instead of the usual trip to the login page.
        this.mobileAppRedirectUrl = (res as any)?.data?.mobileAppRedirectUrl || null;

        let result = `${res}`.toLowerCase() === ClaimValue.TRUE;
        if (isChangeEmail) {
          document.body.classList.add('change-email-veriifed');
          if (res?.success) {
            result = true;
          } else {
            result = false;
            this.error$.next(res?.message);
          }
        }
        return result;
      }),
      catchError((err: HttpErrorResponse) => {
        this.redirectsToLogin$.pipe(take(1)).subscribe(redirects => {
          document.body.classList.add('change-email-veriifed');
          const error = err.error || (err as any).Error;
          const errorCode = error.errorCode || error.ErrorCode;
          this.isActiveUser = errorCode === 'SYNERR-0020';
          this.isLinkExpired = errorCode === 'SYNERR-0065';
          this.isDuplicateError = errorCode === 'SYNERR-0062';
          this.isChangeEmailLinkExpired = errorCode === 'SYNERR-0089';

          if (redirects) {
            if (this.isActiveUser) {
              this.shfyActivationRedirect('is-active-user');
            } else if (this.isLinkExpired) {
              this.shfyActivationRedirect('link-expired');
            } else if (this.isDuplicateError) {
              this.shfyActivationRedirect('is-duplicate-fiscal-code');
            } else if (this.isChangeEmailLinkExpired) {
              this.shfyActivationRedirect('is-change-email-link-expired');
            } else {
              this.shfyActivationRedirect(UNKNOWN_FAILURE_STATUS);
            }
          }

          this.error$.next(err.error?.message);
        });
        return of(false);
      }),
    );
  }

  /**
   * Sends IT / SHFY / global to the login page - where every activation outcome ends for
   * them - and drops this origin's session on the way out.
   *
   * The sign-out belongs to this redirect and not to every trip to the auth host.
   * `GET /Account/Login` signs the user out and deletes their persisted grants before it
   * renders the banner, so all that outlives this navigation is the token sessionStorage
   * holds on the SPA's own origin, which the STS cannot reach from its own. A link to the
   * auth host that does not land on that page has no such sign-out behind it, and clearing
   * the session there would only strand a user who is still signed in.
   */
  private shfyActivationRedirect(status: string = 'success') {
    if (this.hasRedirected) return;

    // SA-21772: an account that signed up from the mobile app has somewhere better to go than
    // the login page - back into the app, already signed in. It is offered as a button rather
    // than taken automatically, so stop here and let the page render instead of navigating.
    // Only on an activation that actually succeeded: every failure status still belongs on the
    // login page, where the banner explaining it lives.
    if (this.mobileAppRedirectUrl && status === 'success') {
      this.loading$.next(false);
      return;
    }

    this.hasRedirected = true;

    const loginUrl = `${this.config.baseAuthUrl}/Account/Login?activation-status=${status}${this.channelQueryParam}${this.orgQueryParam}`;

    // finally, not then: a session that refuses to clear must not strand the user here.
    this.auth.clearLocalSession().finally(() => {
      window.location.href = loginUrl;
    });
  }

  /**
   * Carries the delivery channel over to the login page, and only when the link was texted:
   * every other tenant's banner keys off activation-status alone, so an absent param has to
   * keep meaning what it always did.
   */
  private get channelQueryParam(): string {
    return this.isSmsActivation ? `&channel=${SMS_CHANNEL}` : '';
  }

  /**
   * Columbia lives inside the global deployment, so its login page (and the banner on it) is
   * only drawn when the STS is told the org - the activation link itself does not carry it.
   * `org`, not `org_id`: with no returnUrl, /Account/Login only reads the direct-visit `?org=`.
   * SA-22918: Mexico shares that deployment too, so global names the org the sign-up came with
   * (cached from its `?org-id=`); without one the STS still asks for the country.
   */
  private get orgQueryParam(): string {
    const whiteLabelName = this.config._envConfig?.WhiteLabelName?.toLowerCase();
    const orgId = isColumbiaTenant(whiteLabelName)
      ? COLUMBIA_TENANT
      : whiteLabelName === WhiteLabelNames.GLOBAL
        ? readOrgId()
        : null;
    return orgId ? `&org=${encodeURIComponent(orgId)}` : '';
  }

  redirectToLogin(): void {
    this.auth.startSignoutMainWindow();
  }

  /**
   * Hands the user back to the mobile app, resuming the login their sign-up was started from
   * (SA-21772). The STS re-checks that request is still a pending one for the app before it
   * redirects anywhere, so a stale link simply lands on the usual post-activation page.
   *
   * No sign-out on the way out, unlike {@link shfyActivationRedirect}: this navigation exists
   * precisely to carry the session that was just established into the app.
   */
  returnToApp(): void {
    if (!this.mobileAppRedirectUrl) return;
    window.location.href = this.mobileAppRedirectUrl;
  }
}
